import { XHRRequestCanceler } from '../../../../Libs/xhr/XHRRequestCanceler';
import {
    EnginDataServiceName,
    EnginEndpoints,
    IEnginDataService,
} from '../../../DataServices/EnginDataService';
import { Lazy } from '../../../Helpers/Lazy';
import { ApiDataResponse, BaseApiDataResponse } from '../../../Models/ApiData';
import {
    Engin,
    EnginDetailsApiDataResponse,
    EnginListOrder,
    EnginListResponse,
} from '../../../Models/Engins/Engin';
import { ExportFileType, ExportUtils } from '../../../Utils/ExportUtils';
import { IoC } from '../../ServicesContainer';
import { BaseReduxService } from '../Api/BaseReduxService';
import { AuthServiceName, IAuthService } from '../Authentication/AuthenticationService';
import { DownloadServiceName, IDownloadService } from '../Download/DownloadService';
import { I18nServiceName, II18nService } from '../I18n/I18nService';

export interface IEnginService {
    listEngin: (
        cts: XHRRequestCanceler,
        params: {
            search?: string;
            order?: EnginListOrder;
            is_actif?: boolean;
        }
    ) => Promise<EnginListResponse>;

    getEngin: (cts: XHRRequestCanceler, enginId: string) => Promise<EnginDetailsApiDataResponse>;

    updateEngin: (
        enginId: string,
        engin: Engin,
        cts: XHRRequestCanceler
    ) => Promise<BaseApiDataResponse>;

    createEngin: (engin: Engin, cts: XHRRequestCanceler) => Promise<ApiDataResponse<string>>;

    exportEnginList: (cts: XHRRequestCanceler, search: string, order?: EnginListOrder, is_actif?: boolean) => void;
}

class EnginService extends BaseReduxService implements IEnginService {
    private readonly enginDataService: Lazy<IEnginDataService>;
    private readonly i18nService: Lazy<II18nService>;
    private readonly downloadService: Lazy<IDownloadService>;
    private readonly authService: Lazy<IAuthService>;

    constructor() {
        super();
        this.enginDataService = IoC.getLazy<IEnginDataService>(EnginDataServiceName);
        this.i18nService = IoC.getLazy<II18nService>(I18nServiceName);
        this.downloadService = IoC.getLazy<IDownloadService>(DownloadServiceName);
        this.authService = IoC.getLazy<IAuthService>(AuthServiceName);
    }

    public listEngin = async (
        cts: XHRRequestCanceler,
        params: {
            search?: string;
            order?: EnginListOrder;
            is_actif?: boolean;
        }
    ) => {
        try {
            return this.enginDataService.value().listEngin(
                {
                    search: params.search,
                    order: params.order,
                    is_actif: params.is_actif
                },
                cts
            );
        } catch (err) {
            return Promise.reject(err);
        }
    };

    public getEngin = (cts: XHRRequestCanceler, enginId: string) => {
        try {
            return this.enginDataService.value().getEngin(cts, enginId);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    public updateEngin = async (enginId: string, engin: Engin, cts: XHRRequestCanceler) => {
        try {
            const response = await this.enginDataService.value().updateEngin(enginId, engin, cts);
            if (!response.is_success) {
                this.toastApiError(
                    this.i18nService.value().getString(`Error_${response.error_code}` || '')
                );
            }
            return Promise.resolve(response);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    public createEngin = async (
        engin: Engin,
        cts: XHRRequestCanceler
    ): Promise<ApiDataResponse<string>> => {
        try {
            const response = await this.enginDataService.value().createEngin(engin, cts);
            if (!response.is_success) {
                this.toastApiError(
                    this.i18nService.value().getString(`Error_${response.error_code}` || '')
                );
            }
            return Promise.resolve(response);
        } catch (err) {
            this.toastApiError(this.i18nService.value().getString(`Error_${err.error_code}` || ''));
            return Promise.reject(err);
        }
    };

    public exportEnginList = async (
        cts: XHRRequestCanceler,
        search: string,
        order?: EnginListOrder | undefined,
        is_actif?: boolean
    ): Promise<void> => {
        try {
            const params: { [key: string]: string } = {};
            params['search'] = search;
            if (order) {
                params['order'] = order.valueOf();
            }
            if (is_actif) {
                params['is_actif'] = is_actif.valueOf().toString();
            }
            const url: string = ExportUtils.getExportUrlWithParams(
                {
                    url: EnginEndpoints.EXPORT_ENGIN_LIST,
                    auth: await this.authService.value().getAuthorization()
                },
                params
            );
            this.downloadService.value().downloadFile(url, 'Engins', ExportFileType.EXCEL, cts);
        } catch (err) {
            Promise.reject(err);
        }
    };
}

const EnginServiceName = 'EnginService';
export { EnginService, EnginServiceName };
